import { useState, useEffect } from 'react';
import { unifiedApiService } from '../../services/unified';
import type { Resume, ResumeProcessingStatus as ProcessingStatus } from '../../types/resume';

interface ResumeProcessingStatusProps {
  resume: Resume;
  onStatusUpdate?: (resume: Resume) => void;
  onRetry?: (resumeId: string) => void;
  className?: string;
}

export function ResumeProcessingStatus({ 
  resume, 
  onStatusUpdate, 
  onRetry,
  className = '' 
}: ResumeProcessingStatusProps) {
  const [status, setStatus] = useState<ProcessingStatus | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let intervalId: number | null = null;

    const fetchStatus = async () => {
      try {
        const statusData = await unifiedApiService.resume.getProcessingStatus(resume.id);
        // Cast to component's expected type
        setStatus(statusData as any);
        
        // If status changed, notify parent
        if (statusData.status !== resume.status && onStatusUpdate) {
          onStatusUpdate({ ...resume, status: statusData.status as any });
        }

        // Stop polling if processing is complete or failed
        if (statusData.status === 'scanned' || statusData.status === 'error') {
          if (intervalId) {
            clearInterval(intervalId);
          }
        }
      } catch (err) {
        const error = err as Error;
        setError(error.message || 'Failed to fetch processing status');
        if (intervalId) {
          window.clearInterval(intervalId);
        }
      }
    };

    // Only fetch and poll if resume is in a processing state
    if (resume.status === 'processing' || resume.status === 'submitted') {
      // Initial fetch
      fetchStatus();

      // Poll for updates
      intervalId = window.setInterval(fetchStatus, 3000); // Poll every 3 seconds
    }

    return () => {
      if (intervalId) {
        window.clearInterval(intervalId);
      }
    };
  }, [resume.id, resume.status, onStatusUpdate]);

  const handleRetry = async () => {
    if (!onRetry) return;

    setIsRetrying(true);
    setError(null);

    try {
      await unifiedApiService.resume.retryProcessing(resume.id, { force_reprocess: true });
      onRetry(resume.id);
    } catch (err) {
      const error = err as Error;
      setError(error.message || 'Failed to retry processing');
    } finally {
      setIsRetrying(false);
    }
  };

  const getStatusIcon = (currentStatus: Resume['status']) => {
    switch (currentStatus) {
      case 'submitted':
        return (
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
        );
      case 'processing':
        return (
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-yellow-600"></div>
        );
      case 'scanned':
        return (
          <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        );
      case 'error':
        return (
          <svg className="h-5 w-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        );
      default:
        return null;
    }
  };

  const getStatusText = (currentStatus: Resume['status']) => {
    switch (currentStatus) {
      case 'submitted':
        return 'Submitted for processing';
      case 'processing':
        return status?.stage || 'Processing with AI';
      case 'scanned':
        return 'Analysis complete';
      case 'error':
        return 'Processing failed';
      default:
        return 'Unknown status';
    }
  };

  const getStatusColor = (currentStatus: Resume['status']) => {
    switch (currentStatus) {
      case 'submitted':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'processing':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'scanned':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'error':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const currentStatus = status?.status || resume.status;

  return (
    <div className={`${className}`}>
      <div className={`inline-flex items-center px-3 py-2 rounded-md border ${getStatusColor(currentStatus)}`}>
        <div className="flex-shrink-0 mr-2">
          {getStatusIcon(currentStatus)}
        </div>
        <div className="flex-1">
          <p className="text-sm font-medium">
            {getStatusText(currentStatus)}
          </p>
          {status?.progress && currentStatus === 'processing' && (
            <div className="mt-1">
              <div className="bg-white bg-opacity-50 rounded-full h-1.5">
                <div
                  className="bg-current h-1.5 rounded-full transition-all duration-300"
                  style={{ width: `${status.progress}%` }}
                ></div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Error message and retry button */}
      {currentStatus === 'error' && (
        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-700">
            {status?.errorMessage || resume.errorMessage || 'Processing failed due to an unknown error'}
          </p>
          {onRetry && (
            <button
              onClick={handleRetry}
              disabled={isRetrying}
              className="mt-2 inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isRetrying ? (
                <>
                  <div className="animate-spin rounded-full h-3 w-3 border-b border-red-700 mr-1"></div>
                  Retrying...
                </>
              ) : (
                <>
                  <svg className="h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Retry Processing
                </>
              )}
            </button>
          )}
        </div>
      )}

      {/* Success message with extracted info preview */}
      {currentStatus === 'scanned' && resume.parsedData && (
        <div className="mt-3 space-y-3">
          {/* Analysis Complete */}
          <div className="p-3 bg-green-50 border border-green-200 rounded-md">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h4 className="text-sm font-medium text-green-800">Analysis Complete</h4>
                {resume.score && (
                  <p className="text-sm text-green-700 mt-1">
                    Resume Score: <span className="font-semibold">{resume.score}/10</span>
                  </p>
                )}
                <div className="mt-2 text-xs text-green-600">
                  <p><strong>Extracted Information:</strong></p>
                  <ul className="mt-1 space-y-1">
                    {resume.parsedData.name && <li>• Name: {resume.parsedData.name}</li>}
                    {resume.parsedData.email && <li>• Email: {resume.parsedData.email}</li>}
                    {resume.parsedData.skills && resume.parsedData.skills.length > 0 && (
                      <li>• Skills: {resume.parsedData.skills.slice(0, 3).join(', ')}{resume.parsedData.skills.length > 3 ? '...' : ''}</li>
                    )}
                    {resume.parsedData.experience && resume.parsedData.experience.length > 0 && (
                      <li>• Experience: {resume.parsedData.experience.length} position{resume.parsedData.experience.length !== 1 ? 's' : ''}</li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Strengths */}
          {resume.parsedData.strengths && resume.parsedData.strengths.length > 0 && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <h4 className="text-sm font-medium text-blue-800">Strengths</h4>
                  <ul className="mt-1 text-xs text-blue-600 space-y-1">
                    {resume.parsedData.strengths.map((strength, idx) => (
                      <li key={idx}>• {strength}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Areas for Improvement */}
          {resume.parsedData.areas_for_improvement && resume.parsedData.areas_for_improvement.length > 0 && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-md">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <h4 className="text-sm font-medium text-amber-800">Areas for Improvement</h4>
                  <ul className="mt-1 text-xs text-amber-600 space-y-1">
                    {resume.parsedData.areas_for_improvement.map((area, idx) => (
                      <li key={idx}>• {area}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Score Breakdown */}
          {resume.parsedData.score_breakdown && (
            <div className="p-3 bg-gray-50 border border-gray-200 rounded-md">
              <h4 className="text-sm font-medium text-gray-800 mb-2">Score Breakdown</h4>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {resume.parsedData.score_breakdown.skills_match !== undefined && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Skills Match:</span>
                    <span className="font-medium text-gray-800">{resume.parsedData.score_breakdown.skills_match}/10</span>
                  </div>
                )}
                {resume.parsedData.score_breakdown.experience_quality !== undefined && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Experience:</span>
                    <span className="font-medium text-gray-800">{resume.parsedData.score_breakdown.experience_quality}/10</span>
                  </div>
                )}
                {resume.parsedData.score_breakdown.education !== undefined && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Education:</span>
                    <span className="font-medium text-gray-800">{resume.parsedData.score_breakdown.education}/10</span>
                  </div>
                )}
                {resume.parsedData.score_breakdown.presentation !== undefined && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Presentation:</span>
                    <span className="font-medium text-gray-800">{resume.parsedData.score_breakdown.presentation}/10</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}
    </div>
  );
}